import { cn } from "@/utils/cn";

/**
 * 패널/카드 네 모서리에 장식 파츠를 얹는 오버레이.
 * {@code public/art/ui-ornaments/*.png} 시트 크롭 자산 사용.
 *
 * 사용처:
 * - 결과 카드 (RunResult outcome grade)
 * - 친밀도 회고 패널
 * - 향후 HUD 메인 패널 헤더
 */

type Variant = "large" | "mid" | "bottom";

interface Props {
    variant?: Variant;
    className?: string;
    /** 상단 프레임 캡 (헤더 타이틀 바) — "light" 기본 · "red" 경고/운명 강조 */
    topCap?: "none" | "light" | "red";
    /** 배지 겸용 리본 명패 — 섹션 라벨로 중앙 상단에 */
    plaque?: boolean;
    /** 프레임 모드 — 시트 크롭 PNG 를 네 모서리에 배치 */
    corners?: boolean;
    /** 가운데 중심 크레스트 (희귀 강조) */
    crest?: "none" | "major" | "minor";
    /** 하트 배지 (친밀도 강조) — 우상단에 */
    heart?: boolean;
    size?: number;
}

const CORNER_SRC: Record<Variant, { tl: string; tr: string; bl: string; br: string }> = {
    large: {
        tl: "/art/ui-ornaments/corner-top-left-large.png",
        tr: "/art/ui-ornaments/corner-top-right-large.png",
        bl: "/art/ui-ornaments/corner-bottom-left.png",
        br: "/art/ui-ornaments/corner-bottom-inner.png",
    },
    mid: {
        tl: "/art/ui-ornaments/corner-left-mid.png",
        tr: "/art/ui-ornaments/corner-right-mid.png",
        bl: "/art/ui-ornaments/corner-bottom-left.png",
        br: "/art/ui-ornaments/corner-bottom-inner.png",
    },
    bottom: {
        tl: "/art/ui-ornaments/corner-bottom-left.png",
        tr: "/art/ui-ornaments/corner-bottom-inner.png",
        bl: "/art/ui-ornaments/corner-bottom-left.png",
        br: "/art/ui-ornaments/corner-bottom-inner.png",
    },
};

export function OrnateFrame({
    variant = "mid",
    className,
    topCap = "none",
    plaque = false,
    corners = true,
    crest = "none",
    heart = false,
    size = 68,
}: Props) {
    const s = CORNER_SRC[variant];
    const swap = (e: React.SyntheticEvent<HTMLImageElement>) => {
        (e.target as HTMLImageElement).style.display = "none";
    };

    return (
        <div
            aria-hidden
            className={cn("pointer-events-none absolute inset-0 overflow-visible", className)}
        >
            {corners && (
                <>
                    <img src={s.tl} alt="" onError={swap} style={{
                        position: "absolute", top: -6, left: -6,
                        width: size, height: size, objectFit: "contain", opacity: 0.85,
                    }} />
                    <img src={s.tr} alt="" onError={swap} style={{
                        position: "absolute", top: -6, right: -6,
                        width: size, height: size, objectFit: "contain", opacity: 0.85,
                        transform: "scaleX(-1)",
                    }} />
                    <img src={s.bl} alt="" onError={swap} style={{
                        position: "absolute", bottom: -6, left: -6,
                        width: size * 0.8, height: size * 0.8, objectFit: "contain", opacity: 0.75,
                    }} />
                    <img src={s.br} alt="" onError={swap} style={{
                        position: "absolute", bottom: -6, right: -6,
                        width: size * 0.8, height: size * 0.8, objectFit: "contain", opacity: 0.75,
                        transform: "scaleX(-1)",
                    }} />
                </>
            )}
            {topCap !== "none" && (
                <img
                    src={topCap === "light" ? "/art/ui-ornaments/frame-cap-top-light.png" : "/art/ui-ornaments/frame-cap-top-red.png"}
                    alt="" onError={swap}
                    style={{
                        position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                        width: "70%", maxWidth: 520, opacity: 0.9, objectFit: "contain",
                    }}
                />
            )}
            {plaque && (
                <img
                    src="/art/ui-ornaments/ribbon-plaque.png"
                    alt="" onError={swap}
                    style={{
                        position: "absolute", top: -22, left: "50%", transform: "translateX(-50%)",
                        height: 42, opacity: 0.95, objectFit: "contain",
                    }}
                />
            )}
            {crest !== "none" && (
                <img
                    src={crest === "major" ? "/art/ui-ornaments/crest-major.png" : "/art/ui-ornaments/crest-minor.png"}
                    alt="" onError={swap}
                    style={{
                        position: "absolute", top: -30, left: "50%", transform: "translateX(-50%)",
                        width: crest === "major" ? 78 : 48, objectFit: "contain", opacity: 0.95,
                        filter: "drop-shadow(0 0 14px rgba(201,106,59,0.45))",
                    }}
                />
            )}
            {heart && (
                <img
                    src="/art/ui-ornaments/badge-heart.png"
                    alt="" onError={swap}
                    style={{
                        position: "absolute", top: -14, right: -10,
                        width: 38, opacity: 0.9, objectFit: "contain",
                        filter: "drop-shadow(0 0 10px rgba(201,106,59,0.45))",
                    }}
                />
            )}
        </div>
    );
}
